import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, BookOpen, CheckCircle2, Clock, GraduationCap, Layers, Loader2, Lock, PlayCircle, ShieldCheck, Users } from 'lucide-react';
import { useAuth } from '../auth/AuthContext';
import { getCourseWithContent, getEnrollment, enrollInCourse } from '../../lib/lms';
import { lmsHref } from '../../lib/surface';
import type { LmsCourse } from '../../types/lms';
import EnrollmentModal from '../enrollment/EnrollmentModal';
import ReviewsSection from './ReviewsSection';
import { Panel, PanelHead, Stamp } from '../lms/ui';
import { CourseDetailSkeleton } from '../lms/Skeletons';

interface LmsCourseDetailProps {
  id: string;
}

const LmsCourseDetail: React.FC<LmsCourseDetailProps> = ({ id }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [course, setCourse] = useState<LmsCourse | null>(null);
  const [loading, setLoading] = useState(true);
  const [enrolled, setEnrolled] = useState(false);
  const [enrolling, setEnrolling] = useState(false);
  const [error, setError] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0 });
    let cancelled = false;
    setLoading(true);
    void getCourseWithContent(id).then((c) => {
      if (!cancelled) {
        setCourse(c);
        setLoading(false);
      }
    }).catch(() => {
      if (!cancelled) {
        setCourse(null);
        setLoading(false);
      }
    });
    return () => { cancelled = true; };
  }, [id]);

  useEffect(() => {
    let cancelled = false;
    if (!user || !id) {
      setEnrolled(false);
      return;
    }
    void getEnrollment(user.id, id).then((e) => {
      if (!cancelled) {
        const row = e as unknown as { status?: string } | null;
        setEnrolled(!!row && row.status !== 'revoked');
      }
    }).catch(() => undefined);
    return () => { cancelled = true; };
  }, [user, id]);

  const lessonCount = course?.modules.reduce((n, m) => n + m.lessons.length, 0) ?? 0;
  const isFree = (course?.price_value ?? 1) === 0;

  const freeEnroll = async () => {
    if (!course) return;
    if (!user) {
      window.location.assign(lmsHref(`/signup?course=${encodeURIComponent(course.id)}`));
      return;
    }
    setEnrolling(true);
    setError('');
    try {
      await enrollInCourse(user.id, course.id);
      setEnrolled(true);
      navigate(`/learn/${course.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not enroll. Please try again.');
    } finally {
      setEnrolling(false);
    }
  };

  if (loading) {
    return <CourseDetailSkeleton />;
  }

  if (!course) {
    return (
      <div className="border border-rule bg-white px-6 py-16 text-center">
        <p className="text-[16px] font-semibold text-ink">Programme not found</p>
        <p className="mt-1 text-[13px] text-ink-2">This programme may have been unpublished or moved.</p>
        <Link to="/courses" className="lms-btn-secondary mt-5">
          <ArrowLeft className="h-4 w-4" strokeWidth={1.5} /> Programme register
        </Link>
      </div>
    );
  }

  return (
    <div>
      <Link to="/courses" className="inline-flex items-center gap-1.5 text-[12px] font-medium text-ink-2 hover:text-ink">
        <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.5} /> Programme register
      </Link>

      <div className="mt-5 flex flex-col gap-8 lg:grid lg:grid-cols-[minmax(0,1fr)_320px] lg:items-start">
        <div className="order-0 min-w-0 lg:col-start-1 lg:row-start-1">
          <div className="flex flex-wrap items-center gap-3">
            <Stamp tone="issued">{course.category || 'Programme'}</Stamp>
            {course.level && <Stamp tone="neutral">{course.level}</Stamp>}
            <span className="font-mono text-[9.5px] uppercase tracking-[0.12em] text-ink-2">
              LSEM-{course.id.replace(/[^a-zA-Z0-9]/g, '').slice(0, 6).toUpperCase()}
            </span>
          </div>
          <h1 className="mt-4 max-w-[24ch] text-[clamp(2rem,3.6vw,3rem)] font-semibold leading-[1.06] tracking-[-0.03em] text-ink">
            {course.title}
          </h1>
          <p className="mt-3 max-w-[60ch] text-[16px] leading-7 text-ink-2">{course.tagline || course.description}</p>

          <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 border-y border-rule py-3 text-[12px] text-ink-2">
            <span className="inline-flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" strokeWidth={1.5} /> {course.duration || 'Self-paced'}</span>
            <span className="inline-flex items-center gap-1.5"><Layers className="h-3.5 w-3.5" strokeWidth={1.5} /> {course.modules.length} modules</span>
            <span className="inline-flex items-center gap-1.5"><BookOpen className="h-3.5 w-3.5" strokeWidth={1.5} /> {lessonCount} lessons</span>
            <span className="inline-flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5" strokeWidth={1.5} /> Verified certificate</span>
          </div>
        </div>

        <div className="order-2 min-w-0 space-y-6 lg:order-none lg:col-start-1 lg:row-start-2">
          <Panel>
            <PanelHead title="About this programme" />
            <div className="px-5 py-5">
              <p className="max-w-[70ch] whitespace-pre-wrap text-[13.5px] leading-6 text-ink-2">
                {course.description || course.tagline || 'Full programme details will be published soon.'}
              </p>
            </div>
          </Panel>

          <Panel>
            <PanelHead
              title="Curriculum"
              action={<span className="font-mono text-[9.5px] uppercase tracking-[0.12em] text-ink-2">{course.modules.length} modules · {lessonCount} lessons</span>}
            />
            {course.modules.length === 0 ? (
              <p className="px-5 py-6 text-[13px] text-ink-2">Curriculum is being finalised. Enroll to be notified when lessons go live.</p>
            ) : (
              <div className="divide-y divide-rule">
                {course.modules.map((m, mi) => (
                  <div key={m.id} className="grid gap-3 px-5 py-4 sm:grid-cols-[36px_minmax(0,1fr)]">
                    <span className="font-mono text-[10px] font-semibold text-ink-2">{String(mi + 1).padStart(2, '0')}</span>
                    <div>
                      <p className="text-[14px] font-semibold leading-5 text-ink">{m.title}</p>
                      {m.description && <p className="mt-1 text-[12px] leading-5 text-ink-2">{m.description}</p>}
                      <ul className="mt-2 space-y-1.5">
                        {m.lessons.map((l) => (
                          <li key={l.id} className="flex items-center gap-2.5 text-[12.5px] text-ink">
                            {l.is_free ? (
                              <PlayCircle className="h-3.5 w-3.5 flex-none text-pine" strokeWidth={1.5} />
                            ) : (
                              <Lock className="h-3.5 w-3.5 flex-none text-ink-2" strokeWidth={1.5} />
                            )}
                            <span className="min-w-0 flex-1">{l.title}</span>
                            {l.duration && <span className="tnum font-mono text-[9.5px] text-ink-2">{l.duration}</span>}
                            {l.is_free && <Stamp tone="cleared">Preview</Stamp>}
                          </li>
                        ))}
                        {m.lessons.length === 0 && <li className="text-[12px] text-ink-2">Lessons coming soon.</li>}
                      </ul>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Panel>

          <ReviewsSection courseId={course.id} />
        </div>


        <aside className="order-1 space-y-4 lg:order-none lg:col-start-2 lg:row-span-2 lg:row-start-1 lg:sticky lg:top-24">
          <Panel>
            <div className="border-b border-rule px-5 py-4">
              <p className="font-mono text-[9px] uppercase tracking-[0.14em] text-ink-2">Programme tuition</p>
              <p className="tnum mt-1 text-[30px] font-semibold leading-none tracking-[-0.03em] text-ink">
                {isFree ? 'Free' : `₦${course.price_value.toLocaleString('en-NG')}`}
              </p>
              <p className="mt-1.5 text-[11.5px] text-ink-2">
                {isFree ? 'No payment required' : 'One payment · lifetime classroom access'}
              </p>
            </div>
            <div className="space-y-3 px-5 py-4">
              {error && <p className="border border-danger/25 bg-danger-soft px-3 py-2 text-[12px] text-danger">{error}</p>}
              {enrolled ? (
                <a href={lmsHref(`/learn/${course.id}`)} className="lms-btn-primary w-full">
                  <PlayCircle className="h-4 w-4" strokeWidth={1.5} /> Continue learning
                </a>
              ) : isFree ? (
                <button type="button" onClick={() => void freeEnroll()} disabled={enrolling} className="lms-btn-stamp w-full">
                  {enrolling ? <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} /> : <CheckCircle2 className="h-4 w-4" strokeWidth={1.5} />}
                  {enrolling ? 'Enrolling…' : 'Enroll free'}
                </button>
              ) : (
                <button type="button" onClick={() => setIsModalOpen(true)} className="lms-btn-stamp w-full">
                  <GraduationCap className="h-4 w-4" strokeWidth={1.5} /> Enroll now
                </button>
              )}
              {!user && (
                <p className="text-center text-[11px] text-ink-2">
                  {isFree ? (
                    <><a href={lmsHref(`/signup?course=${encodeURIComponent(course.id)}`)} className="font-semibold text-pine hover:underline">Create your student account</a> to open this free programme, or <a href={lmsHref('/login')} className="font-semibold text-pine hover:underline">sign in</a></>
                  ) : (
                    <>Already have a student account? <a href={lmsHref('/login')} className="font-semibold text-pine hover:underline">Sign in</a>. You can also pay now; we create your account after checkout.</>
                  )}
                </p>
              )}
              <p className="flex items-center justify-center gap-1.5 text-[10.5px] text-ink-2">
                <ShieldCheck className="h-3.5 w-3.5 text-success" strokeWidth={1.5} /> Secure payment through Paystack
              </p>
            </div>
            <ul className="divide-y divide-rule border-t border-rule">
              {['Self-paced video lessons', 'Downloadable resources', 'Quizzes and assignments', 'Verified certificate'].map((f) => (
                <li key={f} className="flex items-start gap-2 px-5 py-2.5 text-[11.5px] text-ink">
                  <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 flex-none text-success" strokeWidth={1.5} /> {f}
                </li>
              ))}
            </ul>
          </Panel>

          <Panel>
            <PanelHead title="Admissions" />
            <div className="px-5 py-4">
              <p className="text-[12.5px] leading-5 text-ink-2">Questions about fit, workload or payment? The school can advise before you commit.</p>
              <Link to="/contact" className="mt-3 inline-flex items-center gap-1.5 text-[12px] font-semibold text-pine hover:underline">
                Contact the school <ArrowRight className="h-3.5 w-3.5" strokeWidth={1.5} />
              </Link>
            </div>
          </Panel>
        </aside>
      </div>

      <EnrollmentModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        courseId={course.id}
        courseTitle={course.title}
        coursePrice={`₦${course.price_value.toLocaleString('en-NG')}`}
        priceValue={course.price_value}
      />
    </div>
  );
};

export default LmsCourseDetail;
